"use client";
import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { buttonVariants } from "./button";
import { cn } from "@/utils";

type ButtonVariant =
  | "default"
  | "outline"
  | "ghost"
  | "link"
  | "destructive"
  | "secondary"
  | null
  | undefined;

type DatePickerProps = {
  selectedDate?: Date;
  onDateSelect?: (date: Date) => void;
  onMonthForward?: () => void;
  onMonthBackward?: () => void;
  callbacks?: {
    yearLabel?: (year: number) => string;
    monthLabel?: (year: number, monthIndex: number) => string; // 0-11
    dayLabel?: (date: Date) => string | number; // สำหรับ custom แสดงเลขวัน
    weekdayLabel?: (index0Mon: number) => string; // 0 = Mon ... 6 = Sun
  };
  variant?: {
    day?: {
      main?: ButtonVariant;
      selected?: ButtonVariant;
      outside?: ButtonVariant; // วันที่นอกเดือน (optional)
      today?: ButtonVariant; // วันนี้ (optional)
    };
    chevrons?: ButtonVariant;
  };
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
} & React.HTMLAttributes<HTMLDivElement>;

/** ---------- Utils ---------- */
const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const clampToDay = (d?: Date) => (d ? startOfDay(d) : undefined);

const addMonths = (date: Date, n: number) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
};

const daysInMonth = (year: number, monthIndex: number) => new Date(year, monthIndex + 1, 0).getDate();

const DEFAULT_MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", { month: "short" });

/** คืน array 6 แถว x 7 คอลัมน์ ของวันที่ (รวมวันนอกเดือนเพื่อเติมตาราง) */
function buildCalendarGrid(displayed: Date, weekStartsOnMonday = true) {
  const year = displayed.getFullYear();
  const month = displayed.getMonth();

  const firstOfMonth = new Date(year, month, 1);
  const firstWeekday = firstOfMonth.getDay(); // 0 Sun - 6 Sat
  const offset =
    weekStartsOnMonday ?
      firstWeekday === 0 ?
        6
      : firstWeekday - 1
    : firstWeekday;

  const totalDays = daysInMonth(year, month);

  const prevMonth = addMonths(firstOfMonth, -1);
  const prevMonthDays = daysInMonth(prevMonth.getFullYear(), prevMonth.getMonth());

  const grid: { date: Date; inCurrentMonth: boolean }[] = [];

  // เติมวันของเดือนก่อนหน้า
  for (let i = offset - 1; i >= 0; i--) {
    const d = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), prevMonthDays - i);
    grid.push({ date: d, inCurrentMonth: false });
  }
  // เติมวันของเดือนนี้
  for (let d = 1; d <= totalDays; d++) {
    grid.push({ date: new Date(year, month, d), inCurrentMonth: true });
  }
  // เติมวันของเดือนถัดไปให้ครบ 42 ช่อง
  while (grid.length < 42) {
    const last = grid[grid.length - 1].date;
    const next = new Date(last);
    next.setDate(last.getDate() + 1);
    grid.push({ date: next, inCurrentMonth: false });
  }

  // แบ่งเป็น 6 แถว
  const rows: (typeof grid)[] = [];
  for (let r = 0; r < 6; r++) rows.push(grid.slice(r * 7, r * 7 + 7));
  return rows;
}

/** ---------- Component ---------- */
export function DatePicker({
  selectedDate,
  onDateSelect,
  callbacks,
  onMonthBackward,
  onMonthForward,
  variant,
  minDate,
  maxDate,
  disabledDates,
  className,
  ...props
}: DatePickerProps) {
  const today = React.useMemo(() => startOfDay(new Date()), []);
  const [displayed, setDisplayed] = React.useState<Date>(
    selectedDate ? new Date(selectedDate) : new Date()
  );

  // sanitize
  minDate = clampToDay(minDate);
  maxDate = clampToDay(maxDate);

  const disabledSet = React.useMemo(() => {
    const s = new Set<string>();
    disabledDates?.forEach((d) => s.add(startOfDay(d).toISOString()));
    return s;
  }, [disabledDates]);

  const displayYear = displayed.getFullYear();
  const displayMonth = displayed.getMonth();

  const weekdays = React.useMemo(() => {
    // จันทร์-อาทิตย์
    const labels: string[] = [];
    for (let i = 0; i < 7; i++) {
      const idx = i; // 0 Mon ... 6 Sun
      const fallback = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][idx];
      labels.push(callbacks?.weekdayLabel?.(idx) ?? fallback);
    }
    return labels;
  }, [callbacks]);

  const grid = React.useMemo(() => buildCalendarGrid(displayed, true), [displayed]);

  const isDateDisabled = (date: Date) => {
    const d = startOfDay(date);
    if (minDate && d < minDate) return true;
    if (maxDate && d > maxDate) return true;
    if (disabledSet.has(d.toISOString())) return true;
    return false;
  };

  const minYear = minDate?.getFullYear();
  const maxYear = maxDate?.getFullYear();

  const getMonthLabel = React.useCallback(
    (year: number, monthIndex: number) => {
      const label = callbacks?.monthLabel?.(year, monthIndex);
      if (label === null || label === undefined) {
        return DEFAULT_MONTH_FORMATTER.format(new Date(year, monthIndex, 1));
      }
      return label;
    },
    [callbacks]
  );

  const getYearLabel = React.useCallback(
    (year: number) => {
      const label = callbacks?.yearLabel?.(year);
      if (label === null || label === undefined) return String(year);
      return typeof label === "string" ? label : String(label);
    },
    [callbacks]
  );

  const clampMonthToBounds = React.useCallback(
    (year: number, monthIndex: number) => {
      let output = monthIndex;
      if (typeof minYear === "number" && year === minYear && minDate) {
        const boundary = minDate.getMonth();
        if (output < boundary) output = boundary;
      }
      if (typeof maxYear === "number" && year === maxYear && maxDate) {
        const boundary = maxDate.getMonth();
        if (output > boundary) output = boundary;
      }
      return output;
    },
    [maxDate, minDate, maxYear, minYear]
  );

  const yearOptions = React.useMemo(() => {
    const fallbackWindow = 50;
    const start = typeof minYear === "number" ? minYear : displayYear - fallbackWindow;
    const end = typeof maxYear === "number" ? maxYear : displayYear + fallbackWindow;

    const years: number[] = [];
    for (let year = start; year <= end; year++) {
      years.push(year);
    }

    if (!years.includes(displayYear)) {
      years.push(displayYear);
      years.sort((a, b) => a - b);
    }

    if (selectedDate) {
      const selectedYear = selectedDate.getFullYear();
      if (!years.includes(selectedYear)) {
        years.push(selectedYear);
        years.sort((a, b) => a - b);
      }
    }

    return years;
  }, [displayYear, maxYear, minYear, selectedDate]);

  const monthOptions = React.useMemo(() => {
    const months = Array.from({ length: 12 }, (_, monthIndex) => {
      const disabled =
        (typeof minYear === "number" &&
          displayYear === minYear &&
          minDate &&
          monthIndex < minDate.getMonth()) ||
        (typeof maxYear === "number" &&
          displayYear === maxYear &&
          maxDate &&
          monthIndex > maxDate.getMonth());

      return {
        value: monthIndex,
        disabled,
        label: getMonthLabel(displayYear, monthIndex)
      };
    });

    const currentMonthExists = months.some((option) => option.value === displayMonth);
    if (!currentMonthExists) {
      months.push({
        value: displayMonth,
        disabled: false,
        label: getMonthLabel(displayYear, displayMonth)
      });
    }

    return months
      .sort((a, b) => a.value - b.value)
      .filter((option, index, arr) => index === 0 || option.value !== arr[index - 1].value);
  }, [displayMonth, displayYear, getMonthLabel, maxDate, maxYear, minDate, minYear]);

  const handleMonthSelect = React.useCallback((nextValue: string) => {
    const nextMonth = Number.parseInt(nextValue, 10);
    if (Number.isNaN(nextMonth)) return;

    setDisplayed((prev) => {
      const next = new Date(prev);
      next.setDate(1);
      next.setMonth(nextMonth);
      return next;
    });
  }, []);

  const handleYearSelect = React.useCallback(
    (nextValue: string) => {
      const nextYear = Number.parseInt(nextValue, 10);
      if (Number.isNaN(nextYear)) return;

      setDisplayed((prev) => {
        const next = new Date(prev);
        next.setFullYear(nextYear);
        const adjustedMonth = clampMonthToBounds(nextYear, next.getMonth());
        next.setMonth(adjustedMonth);
        next.setDate(1);
        return next;
      });
    },
    [clampMonthToBounds]
  );

  return (
    <div className={cn("min-w-[260px] w-[300px] p-3", className)} {...props}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => {
            setDisplayed(addMonths(displayed, -1));
            onMonthBackward?.();
          }}
          className={cn(
            buttonVariants({ variant: variant?.chevrons ?? "outline" }),
            "inline-flex h-8 w-8 items-center justify-center p-0"
          )}
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4 opacity-70" />
        </button>

        <Select value={String(displayMonth)} onValueChange={handleMonthSelect}>
          <SelectTrigger
            size="sm"
            aria-label="Select month"
            className="h-8 min-w-[110px] justify-between rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 shadow-none focus-visible:ring-0"
          >
            <SelectValue placeholder={getMonthLabel(displayYear, displayMonth)} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {monthOptions.map((option) => (
              <SelectItem
                className="text-center w-full"
                key={option.value}
                value={String(option.value)}
                disabled={option.disabled}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(displayYear)} onValueChange={handleYearSelect}>
          <SelectTrigger
            size="sm"
            aria-label="Select year"
            className="h-8 min-w-[90px] justify-between rounded-md border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 shadow-none focus-visible:ring-0"
          >
            <SelectValue placeholder={getYearLabel(displayYear)} />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {yearOptions.map((year) => (
              <SelectItem
                key={year}
                value={String(year)}
                disabled={
                  (typeof minYear === "number" && year < minYear) ||
                  (typeof maxYear === "number" && year > maxYear)
                }
              >
                {getYearLabel(year)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          onClick={() => {
            setDisplayed(addMonths(displayed, 1));
            onMonthForward?.();
          }}
          className={cn(
            buttonVariants({ variant: variant?.chevrons ?? "outline" }),
            "inline-flex h-8 w-8 items-center justify-center p-0"
          )}
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4 opacity-70" />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 text-[11px] text-muted-foreground mb-1">
        {weekdays.map((w, i) => (
          <div key={i} className="text-center py-1">
            {w}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="grid grid-cols-7 gap-1">
        {grid.map((row, r) =>
          row.map(({ date, inCurrentMonth }, c) => {
            const selected = selectedDate && isSameDay(date, selectedDate);
            const isToday = isSameDay(date, today);
            const disabled = isDateDisabled(date);

            // เลือก variant ของปุ่มวัน
            let variantName: ButtonVariant = variant?.day?.main ?? "ghost";
            if (!inCurrentMonth) variantName = variant?.day?.outside ?? "ghost";
            if (isToday && !selected) variantName = variant?.day?.today ?? variantName;
            if (selected) variantName = variant?.day?.selected ?? "default";

            const label = callbacks?.dayLabel?.(date) ?? date.getDate();

            return (
              <button
                key={`${r}-${c}`}
                onClick={() => !disabled && onDateSelect?.(date)}
                disabled={disabled || !inCurrentMonth}
                className={cn(
                  buttonVariants({ variant: variantName }),
                  "h-9 w-full p-0 text-sm font-normal rounded-md transition-all duration-150",
                  "border border-transparent",
                  !inCurrentMonth && "opacity-50 pointer-events-none",
                  selected ? "bg-[#379a2a] text-white" : (
                    "bg-white text-black hover:border-[#379a2a] hover:bg-green-50 active:bg-green-100"
                  )
                )}
                aria-selected={selected}
                aria-label={date.toDateString()}
              >
                {label}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ===== Example usage =====
<DatePicker
  selectedDate={value}
  onDateSelect={(d) => setValue(d)}
  minDate={new Date(2020,0,1)}
  maxDate={new Date(2030,11,31)}
  disabledDates={[new Date(2025,0,1)]}
/>
*/
